import { useCallback, useEffect, useRef, useState } from 'react';

import { useChannel } from 'ably/react';

import { RadioClicksAPI } from '@/api/api.radio.clicks';
import { RadioClickData } from '@/models/radio.click.data';
import { useRadioClicksStore, useUserStore } from '@/store';

export type GroupedRadioClickData = {
  [key: string]: RadioClickData[];
};

const getGroupedData = (
  radioClicks: RadioClickData[],
): GroupedRadioClickData => {
  const response: GroupedRadioClickData = {};

  const groupedUnitShifts = radioClicks.reduce(
    (acc: GroupedRadioClickData, item: RadioClickData) => {
      const date = new Date(item.radioClickTime ?? '');
      const key = `${date.toLocaleDateString()} ${date.getHours()}:${date
        .getMinutes()
        .toString()
        .padStart(2, '0')}`;

      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    },
    {},
  );

  Object.keys(groupedUnitShifts).forEach((key) => {
    if (!response[key]) {
      response[key] = [];
    }
    response[key].push(...groupedUnitShifts[key]);
  });

  return response;
};

/**
 * Sorts the grouped data by date (chronologically, oldest first)
 */
const sortGroupedDataByDate = (
  data: GroupedRadioClickData,
): GroupedRadioClickData => {
  // Parse keys into dates and sort them
  const sortedKeys = Object.keys(data)
    .map((key) => {
      const [datePart, timePart] = key.split(' ');
      const [hours, minutes] = timePart.split(':');

      const date = new Date(datePart);
      date.setHours(parseInt(hours, 10), parseInt(minutes, 10));

      return { key, date };
    })
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  // Create a new sorted object
  const sortedData: GroupedRadioClickData = {};

  // Add each key in sorted order, with sorted items
  for (const { key } of sortedKeys) {
    // Sort the items for this key by their click time
    sortedData[key] = [...data[key]].sort((a, b) => {
      const dateA = new Date(a.radioClickTime ?? '');
      const dateB = new Date(b.radioClickTime ?? '');
      return dateA.getTime() - dateB.getTime();
    });
  }

  return sortedData;
};

export const useRadioClicksData = () => {
  const [rawData, setRawData] = useState<RadioClickData[]>([]);
  const [groupedData, setGroupedData] = useState<GroupedRadioClickData>({});
  const [isFetching, setIsFetching] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { account } = useUserStore();
  const [hasNewData, setHasNewData] = useState(false);
  const prevDataSizeRef = useRef(0);

  useChannel(
    `account:${account?._id}:radio-clicks`,
    ({ data, name }: { data: RadioClickData; name: string }) => {
      if (isFetching) {
        fetchData(true);
        return;
      }
      if (name === 'CREATE') {
        setHasNewData(true);
        prevDataSizeRef.current = new Date().getTime();
        setRawData((prevData) => [...prevData, data]);
        const unsortedData = getGroupedData([...rawData, data]);
        const sortedData = sortGroupedDataByDate(unsortedData);
        setGroupedData(sortedData);
      } else if (name === 'UPDATE') {
        const newData = [...rawData];

        newData.forEach((item, index) => {
          if (item.radioId === data.radioId) {
            newData[index] = {
              ...data,
              radioClickTime: newData[index].radioClickTime,
            };
          }
        });
        setRawData(newData);
        const unsortedData = getGroupedData([...newData]);
        const sortedData = sortGroupedDataByDate(unsortedData);
        setGroupedData(sortedData);
      } else {
        fetchData(true);
      }
    },
  );

  const fetchData = useCallback((withoutLoading = false) => {
    setIsFetching(true);
    if (!withoutLoading) {
      setIsLoading(true);
    }
    RadioClicksAPI.getAll()
      .then((response) => {
        useRadioClicksStore.getState().setCache(response.data);
        setRawData(response.data);
        const unsortedData = getGroupedData(response.data);

        // Sort the grouped data by date
        const sortedData = sortGroupedDataByDate(unsortedData);

        // Check if we have new data
        const totalItems = Object.values(sortedData).reduce(
          (sum, items) => sum + items.length,
          0,
        );

        if (totalItems > prevDataSizeRef.current) {
          setHasNewData(true);
        }

        prevDataSizeRef.current = totalItems;
        setGroupedData(sortedData);
      })
      .finally(() => {
        setIsLoading(false);
        setIsFetching(false);
      });
  }, []);

  // Clear the new data flag
  const acknowledgeNewData = useCallback(() => {
    setHasNewData(false);
  }, []);

  useEffect(() => {
    const { cache, isCacheFresh } = useRadioClicksStore.getState();
    if (isCacheFresh() && cache) {
      setRawData(cache);
      const unsortedData = getGroupedData(cache);
      setGroupedData(sortGroupedDataByDate(unsortedData));
      prevDataSizeRef.current = cache.length;
      setIsLoading(false);
      return;
    }
    fetchData();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchData(true);
    }, 3600000); // Fetch data every 1 hour

    return () => clearInterval(interval);
  }, [fetchData]);

  return {
    groupedData,
    isLoading,
    hasNewData,
    acknowledgeNewData,
  };
};
